import { useAuth } from '../context/AuthContext';
import { useMemo } from 'react';

// Function to check if a menu item path is accessible to the user
const isPathAccessible = (path, allowedPages) => {
  if (!path) return false;
  
  // Master admin can access everything
  if (allowedPages.includes('*')) return true;
  
  // Dashboard access is now controlled by allowedPages - no special treatment
  
  // Check if the exact path is allowed
  if (allowedPages.includes(path)) return true;
  
  // Check if any parent path is allowed (for nested routes)
  const pathSegments = path.split('/').filter(segment => segment);
  for (let i = pathSegments.length; i > 0; i--) {
    const parentPath = pathSegments.slice(0, i).join('/');
    if (allowedPages.includes(parentPath)) return true;
  }
  
  return false;
};

// Recursive function to filter menu items based on user permissions
const filterMenuItems = (menuItems, allowedPages, userRole = null) => {
  if (!menuItems || !Array.isArray(menuItems)) return [];
  
  return menuItems
    .map(item => {
      // Role-specific filtering
      if (userRole === 'custom_role_ck' && item.label === 'Branch') {
        return null; // Hide Branch submenu for CK users
      }
      // Sales and order-receiver must never see Central Kitchen menu
      if ((userRole === 'custom_role_sales' || userRole === 'custom_role_order_receiver') && item.label === 'Central Kitchen') {
        return null;
      }

      // Sales menu: sales user sees Dashboard, New Order, Orders, Revenue; admin sees Orders, Revenue, Edit Menu
      if (item.label === 'Sales' && item.children && Array.isArray(item.children)) {
        const isSalesUser = userRole && String(userRole).toLowerCase() === 'custom_role_sales';
        const isAdminOrMaster = userRole === 'admin' || userRole === 'master_admin';
        let children = item.children;
        if (isSalesUser) {
          children = children.filter(
            (c) =>
              c.path !== '/sales/edit-menu' &&
              (c.path === '/sales/revenue' || isPathAccessible(c.path, allowedPages))
          );
        } else if (isAdminOrMaster) {
          children = children.filter(
            (c) =>
              (c.path === '/sales/orders' || c.path === '/sales/revenue' || c.path === '/sales/edit-menu') &&
              isPathAccessible(c.path, allowedPages)
          );
        } else {
          children = filterMenuItems(item.children, allowedPages, userRole);
        }
        if (children.length > 0) {
          return { ...item, children };
        }
        return null;
      }
      
      // Order Receiver: only for custom_role_order_receiver (hidden from admin/master_admin)
      if (item.label === 'Order Receiver' && item.children) {
        if (userRole === 'admin' || userRole === 'master_admin') return null;
        const canAccess = allowedPages.includes('*') || allowedPages.includes('order-receiver');
        if (!canAccess) return null;
        const filteredChildren = filterMenuItems(item.children, allowedPages, userRole);
        if (filteredChildren.length > 0) return { ...item, children: filteredChildren };
        return null;
      }
      
      // If item has a direct path, check if it's accessible
      if (item.path) {
        if (isPathAccessible(item.path, allowedPages)) {
          return item;
        }
        return null;
      }
      
      // If item has children, recursively filter them
      if (item.children && Array.isArray(item.children)) {
        const filteredChildren = filterMenuItems(item.children, allowedPages, userRole);
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        return null;
      }
      
      return item;
    })
    .filter(Boolean);
};

// Hook to get filtered menu items based on current user permissions
export const useFilteredMenu = (menuItems) => {
  const { getAllowedPages, user } = useAuth();
  
  // Use useMemo to ensure the filtered menu updates when user or allowedPages change
  const filteredMenu = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 useFilteredMenu called with:', { user: !!user, menuItems: !!menuItems });
    }
    
    if (!user || !menuItems) {
      if (process.env.NODE_ENV === 'development') {
        console.log('❌ No user or menuItems, returning empty array');
      }
      return [];
    }
    
    const allowedPages = getAllowedPages();
    if (process.env.NODE_ENV === 'development') {
      console.log('📋 Allowed pages:', allowedPages);
    }
    
    // If no allowed pages, return empty array
    if (!allowedPages || allowedPages.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('❌ No allowed pages, returning empty array');
      }
      return [];
    }
    
    const filtered = filterMenuItems(menuItems, allowedPages, user.role);
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Filtered menu items:', filtered.length);
    }
    return filtered;
  }, [user, menuItems, getAllowedPages]);
  
  return filteredMenu;
};

// Export the filter function for direct use
export { filterMenuItems, isPathAccessible };
